<template>
	<v-chart
		v-if="option !== null"
		ref="chart"
		:option="option"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>




<script>
import { watchEffect } from 'vue'
import VChart from 'vue-echarts'
import { INFLUXDB } from '@/api/influxdb.js'
import { FIELD_NAMES } from '@/api/api.js'
import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { ElectricMeter } from '@/models/electricMeter.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsStore } from '@/stores/StationsStore.js'
import { useStationsHistoryStore } from '@/stores/StationsHistoryStore.js'
import { useStationPassport } from '@/composables/stationPassport.js'
import { useEvents, eventSchema } from '@/composables/events.js'
import {
	capitalizeFirstLetter,
	subtractHoursFromNowDate,
	dateLocalString,
	dateTimeToString,
	chartTimeStr,
	chartDateStr,
	periodDays,
	verboseTimePeriod,
	calculateTimeWindows,
} from '@/helpers.js'

import {
	use,
	graphic,
} from 'echarts/core'
import {
	LineChart
} from 'echarts/charts'
import {
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	ToolboxComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	// MarkLineComponent,
	MarkAreaComponent,
} from 'echarts/components'
import {
	CanvasRenderer,
	SVGRenderer,
} from 'echarts/renderers'
import { ModemUnit } from '@/models/modemUnit'
use([
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	ToolboxComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	// MarkLineComponent,
	MarkAreaComponent,
	LineChart,
	CanvasRenderer,
	SVGRenderer,
])

const GREEN = '#73bf69'
const RED = '#f2495c'
const GRAY = '#5f6368'//'#ccccdc'

const seriesXaxisDivider = 1000

const BARS = {
	[Station.analyticMetrics.successTransactionCntr]: {
		metric: Station.analyticMetrics.successTransactionCntr,
		text: 'Успішно',
		color: '#73bf69',
		valueIndex: 6,
	},
	[Station.analyticMetrics.failConnectCntr]: {
		metric: Station.analyticMetrics.failConnectCntr,
		text: 'Помилка підключення',
		color: '#b877d9',
		valueIndex: 7,
	},
	[Station.analyticMetrics.failReadCntr]: {
		metric: Station.analyticMetrics.failReadCntr,
		text: 'Таймаут відповіді',
		color: '#f2cc0c',
		valueIndex: 8,
	},
	[Station.analyticMetrics.invalidMessagesReceivedCntr]: {
		metric: Station.analyticMetrics.invalidMessagesReceivedCntr,
		text: 'Помилка читання (CRC т ін.)',
		color: '#8ab8ff',
		valueIndex: 9,
	},
	[Station.analyticMetrics.unexpectedlyClosedConnectionCntr]: {
		metric: Station.analyticMetrics.unexpectedlyClosedConnectionCntr,
		text: 'Втрата сокета',
		color: '#f2495c',
		valueIndex: 10,
	},
}

let xAxisDayTmp = null
function timeLabelFormatter(from, now, to) {
	// If period > 6 days
	if (periodDays(from, to) > 6) {
		return `${chartDateStr(now)}`
	}
	// If first axis label in chart or first label in day
	else if( (xAxisDayTmp == null
		|| now.getDate() != xAxisDayTmp.getDate())
		|| (now.getHours() == 0 && now.getMinutes() == 0 && now.getSeconds() == 0)
	) {
		xAxisDayTmp = now
		return `${chartDateStr(now)}`
	} else {
		return `${chartTimeStr(now)}`
	}
}

/*
Render chart item as rectancle.
Start and stop depends on metric time. Clip rectangle if it going out of bounds.
Color depends of metric value.
*/
let barsYAxisMax = 0
let barsSurveyPeriodMax = 0
function renderConnectionChartItem(params, api) {
	const type = api.value(5)
	
	if (type == 'connectionAndRanges') {
		const startTime = api.value(0)
		const categoryId = api.value(1)
		const duration = api.value(2)
		const color = api.value(3)
		// console.log('test', api.value())
		const startPoint = api.coord([startTime, categoryId])
		const endPoint = api.coord([startTime + duration, categoryId])
		const height = api.size([0, 1])[1] * 0.7

		// Create rectangle
		const rect = {
			x: startPoint[0],
			y: startPoint[1] - height / 2,
			width: endPoint[0] - startPoint[0],
			height: height,
		}

		// Coordinates system boundaries (graph area)
		const coordSys = params.coordSys

		// Clip rectangle if it going out of bounds.
		const clippedRect = graphic.clipRectByRect(rect, {
			x: coordSys.x,
			y: coordSys.y,
			width: coordSys.width,
			height: coordSys.height,
		})

		// If no rect
		if (!clippedRect) {
			return
		}

		return {
			type: 'rect',
			shape: clippedRect,
			style: {
				fill: color, // Rectangle color
				stroke: '#ffffff', // Border color
				lineWidth: 0.1, // Border width
				opacity: 0.8, // Border opacity
			},
		}
	} else if (type == 'connectionMetrics') {
		const startTime = api.value(0)
		const categoryId = api.value(1)
		const duration = api.value(2)
		const surveyPeriod = api.value(11)

		// Coordinates system boundaries (graph area)
		const coordSys = params.coordSys
		const startPoint = api.coord([startTime, categoryId])
		const endPoint = api.coord([startTime + duration, categoryId])
		const height = api.size([0, 1])[1]
		const chartVerticalLow = startPoint[1] + height / 2
		const chartVerticalHi = startPoint[1] - height / 2

		if (Math.abs(startPoint[0] - endPoint[0]) < 1) {
			return
		}

		const bars = []
		let barProgress = 0
		for (const bar of Object.values(BARS)) {
			if (api.value(bar.valueIndex) == 0) {
				continue
			}

			// const barStartValue = barProgress
			barProgress += api.value(bar.valueIndex)

			// Create rectangle
			const rect = {
				x: startPoint[0],
				y: chartVerticalLow - (height * (barProgress / barsYAxisMax)),
				width: (endPoint[0] - startPoint[0]) * 0.85,
				height: height * (api.value(bar.valueIndex) / barsYAxisMax),
			}

			// Clip rectangle if it going out of bounds.
			const clippedRect = graphic.clipRectByRect(rect, {
				x: coordSys.x,
				y: coordSys.y,
				width: coordSys.width,
				height: coordSys.height,
			})

			bars.push({
				type: 'rect',
				shape: clippedRect,
				style: {
					fill: bar.color,	// Rectangle color
					stroke: '#ffffff',	// Border color
					lineWidth: 0.1,		// Border width
					opacity: 0.8,		// Border opacity
				},
			})
		}

		// Point - request frequency setting
		const pointX = startPoint[0] + (endPoint[0] - startPoint[0]) * 0.5
		const pointY = chartVerticalLow
			+ (chartVerticalHi - chartVerticalLow)
			* (surveyPeriod / barsSurveyPeriodMax)

		bars.push({
			type: 'circle',
			shape: {
				cx: pointX,
				cy: pointY,
				r: 2.5,
			},
			style: {
				fill: GRAY,
				stroke: GRAY,
				lineWidth: 1,
				opacity: 0.5,
			},
		})

		// If no rect
		if (bars.length == 0) {
			return
		}
		
		return {
			type: 'group',
			children: bars,
		}
	}
}

// Render flag icon settings
const iconSize = 24
const iconPadding = 16

export default {
	LINES_CHART_GRID_INDEX: 0,
	LINES_TOP_X_AXIS_INDEX: 0,
	LINES_BOTTOM_X_AXIS_INDEX: 1,

	SEGMENTS_CHART_GRID_INDEX: 1,
	BARS_CHART_X_AXIS_INDEX: 2,
	SEGMENTS_CHART_X_AXIS_INDEX: 3,

	components: {
		VChart,
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},

		// Chart lines
		// Array of Objects
		// {
		// 		title: 'Захисний потенціал',
		// 		showInLines: true,
		// 		showInTable: true,
		//		showInSegments: true,
		// 		confidenceBand: true,
		// 		yAxis: {
		// 			id: 'line_left',
		// 			side: 'left',
		// 			formatter: (value) => `${value.toFixed(1)} В`,
		// 		},
		// 		lineType: 'solid',
		// 		lineColor: '#5794f2',
		// 		unit: INFLUXDB.station_metrics.service_unit,
		// 		icon: 'service',
		// 		metric: ServiceUnit.metricKeys.eAvg,
		// }
		items: {
			type: Array,
			require: false,
			default: () => Array(),
		},

		resetMetricsData: {
			type: Boolean,
			require: false,
			default: false,
		},

		fillUndefinedAsPrevious: {
			type: Boolean,
			require: false,
			default: true,
		},

		periodFrom: {
			type: Date,
			require: true,
		},

		periodTo: {
			type: Date,
			require: true,
		},

		poiRangeSeconds: {
			type: Number,
			require: true,
		},
	},

	emits: {
		'isLoading': value => typeof value === "boolean",
		'isTable': value => typeof value === 'boolean',
		'pointOfInterest': value => value instanceof Date,
		'updateZoomedArea': value => typeof value === 'function',
	},

	setup(props) {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const historyStore = useStationsHistoryStore()
		const { allEvents: lightningEvents, setFilters } = useEvents()
		// Read lightning events
		watchEffect(() => {
			setFilters({
				fromTime: props.periodFrom,
				toTime: props.periodTo,
				eventTypes: [eventSchema.eventType.allowed.lightning.key, ],
				stationIds: [props.stationId, ],
			})
		})

		const {
			passportHistory,
			isLoading: isPassportHistoryPeriodLoading,
			changePeriod: setPassportHistoryPeriod,
		} = useStationPassport(props.stationId)
		// All falgs
		let flags = []
		// Icons from grouped by ovellaped size flags
		let icons = []
		// Graph configuration
		let option = null

		return {
			capitalizeFirstLetter,
			dateTimeToString,
			dateLocalString,
			subtractHoursFromNowDate,
			Station,
			PowerUnit,
			ElectricMeter,
			accountStore,
			stationsStore,
			historyStore,

			lightningEvents,

			passportHistory,
			isPassportHistoryPeriodLoading,
			setPassportHistoryPeriod,
			flags,
			icons,
			option,
		}
	},

	data() {
		return {
			// option: null,
			segmentsChartType: 'connectionAndRanges', // 'connectionAndRanges', 'connectionMetrics'
			
			// Zoom state
			chartPositionPercent: 50,
			chartRangePercent: 100,		// actual zoom range in %
			// Set first step on double click zooming
			chartZoomStep: 0,

			// Series that hovered by mouse poiner now
			hoveredSeries: null,

			// Table show flag
			isTable: false,
			// Point of interest (Date)
			poi: null,
			
			isLoading: false,
		}
	},

	watch: {
		// Update chart on timeFrom update
		async periodFrom() {
			// console.log(`periodFrom updated? ${!this.isLoading && this.chartInstance() !== undefined}`)
			if (!this.isLoading && this.chartInstance() !== undefined) {
				this.initCharts()
				// this.setDataZoomToDefault() // zoom starts metric fetch
				await this.fetchMetrics()
				this.updateZoomedArea()
				// this.setPoi(this.chartZoomMiddle)
			}

			// this.setPassportHistoryPeriod(this.periodFrom, null)//this.periodTo)
		},

		// Update chart on metrics in storage update
		updateTriger() {
			// console.log('chart updateTriger')
			this.updateAllCharts()
		},

		lightnings() {
			this.updateFlags(this.chartsZoomFrom, this.chartsZoomTo)
		},

		visibleLinesArray() {
			// console.log('updated visibleLinesArray')
			this.fetchMetrics()

			// Show or hide lines on chart
			this.linesArray.forEach((el) => {
				if (el.showInLines) {
					this.chartInstance()?.dispatchAction({
						type: 'legendSelect',
						name: el.title
					})
				} else {
					this.chartInstance()?.dispatchAction({
						type: 'legendUnSelect',
						name: el.title
					})
				}
			})

			this.chartInstance()?.setOption({ graphic: this.buildSideDots() })
		},

		visibleSegmentsArray() {
			this.updateSegmentsChart(this.periodFrom, this.periodTo)
		},
	},

	created() {
		this.$emit('updateZoomedArea', this.updateZoomedArea)

		const xAxisOptions = (id, gridIndex, position) => {
			return {
				id: id,
				gridIndex: gridIndex, // attach to grid
				position: position,
				offset: position == 'top' ? iconSize + 10 : 0,
				type: 'time',
				min: this.periodFrom,
				max: this.periodTo,
				boundaryGap: false,
				splitLine: {
					// For chow virtical axis lines
					show: true,
				},
				data: [],
				axisLine: {
					show: true, // for compatibility with change yAxis settings on click
				},
				axisTick: {
					show: false, // for compatibility with change yAxis settings on click
				},
				axisLabel: {
					// interval: 0, 
					formatter: (value) => {
						var local = new Date(new Date(value).getTime())
						return timeLabelFormatter(this.chartsZoomFrom, local, this.chartsZoomTo)
					},
					hideOverlap: true, // prevent overlpping
					showMinLabel: true,
					showMaxLabel: true,
				},
			}
		}

		const yAxisOptions = (id, gridIndex, position, ) => {
			return {
				id: id,
				show: false,
				gridIndex: gridIndex, // attach to grid
				type: 'value',
				// name: 'Line name',
				nameGap: 50,
				nameLocation: 'center',
				nameTextStyle: {
					fontFamily: 'monospace',
					// fontSize: 14,
					// fontWeight: 'bolder'
				},
				position: position,
				min: 'dataMin',
				max: 'dataMax',
				alignTicks: false,
				// Vertical line of axis
				axisLine: {
					show: true,
					// lineStyle: {
					// 	color: this.$options.TITLE_COLOR,
					// }
				},
				// Grid lines of axis
				splitLine: {
					show: true,
				},
				axisLabel: {
				// 	formatter: '{value} В',
				},
				axisPointer: {
					snap: true
				}
			}
		}

		const lineSeriesGenerator = () => {
			const series = []
			let xAxisPosition = 'top'
			
			for (const line of this.linesArray) {
				// Bind to top and bottom xAxis
				if (xAxisPosition == 'top') {
					xAxisPosition = 'bottom'
				} else {
					xAxisPosition = 'top'
				}
				
				// Line
				series.push({
					id: line.metric,
					name: line.title,
					type: 'line',
					symbolSize: 10,	// points size
					// attach to x axis
					xAxisId: 'line_' + xAxisPosition,
					// attach to y axis
					yAxisId: line.yAxis?.id, 
					data: [],
					smooth: true,
					z: 1,
					itemStyle: {
						color: line.lineColor,
					},
					lineStyle: {
						type: line.lineType,
						color: line.lineColor,
						width: 1.5,
					},
					showSymbol: false, 
					emphasis: {
						// disabled: true, // Disable effect on hover 
						focus: 'series'
					},
				})

				// If line has no confidence band
				if (!line.confidenceBand) {
					continue
				}

				// Confidence band line
				series.push({
					id: line.metric + 'confidence_line',
					name: line.title, // same as main line for show hide with line
					type: 'line',
					// attach to x axis
					xAxisId: 'line_' + xAxisPosition,
					// attach to y axis
					yAxisId: line.yAxis?.id,
					data: [],
					smooth: false,
					z: 1,
					itemStyle: {
						color: line.lineColor,
					},
					lineStyle: {
						type: line.lineType,
						color: line.lineColor,
						width: 1.5,
						opacity: 0,
					},
					stack: line.title + '-band',
					symbol: 'none',
					emphasis: {
						disabled: true, // Disable effect on hover 
					},
					// Hide in legend
					legendHoverLink: false,
					showSymbol: false,
					// legend: { show: false },
				})

				// Confidence band range
				series.push({
					id: line.metric + 'confidence_range',
					name: line.title, // same as main line for show hide with line
					type: 'line',
					// attach to x axis
					xAxisId: 'line_' + xAxisPosition,
					// attach to y axis
					yAxisId: line.yAxis?.id,
					data: [],
					smooth: false,
					z: 1,
					itemStyle: {
						color: line.lineColor,
					},
					lineStyle: {
						type: line.lineType,
						color: line.lineColor,
						width: 1.5,
						opacity: 0,
					},
					stack: line.title + '-band',
					symbol: 'none',
					areaStyle: { // enable area gradient (by visual map)
						color: line.lineColor,
						opacity: 0.15,
					},
				})
			}

			return series
		}

		this.option = {
			// Color line indicators on top left and right
			graphic: this.buildSideDots(),

			legend: {
				show: false,	
				data: this.items.map((el) => el.title),
				selected: Object.fromEntries(this.items.map((el) => [el.title, el.showInLines])),
			},

			dataZoom: [
				// Slider on bottom of the chart
				{
					type: 'slider',
					show: true,
					realtime: true,
					xAxisIndex: [
						this.$options.LINES_TOP_X_AXIS_INDEX,
						this.$options.LINES_BOTTOM_X_AXIS_INDEX,
						this.$options.BARS_CHART_X_AXIS_INDEX,
						this.$options.SEGMENTS_CHART_X_AXIS_INDEX,
					],
					bottom: 10,
					left: 30,
					right: 60,
					height: this.dataZoomHeight,
					dataBackground: {
						areaStyle: {
							opacity: 0.1,
						}
					},
					labelFormatter: function (value, valueStr) {
						const date = new Date(valueStr)
						return `${chartDateStr(date)}\n${chartTimeStr(date)}`
					}
				},
				// For touchscreen
				{
					type: 'inside',
					disabled: true,
				},
			],

			// Set lines chart and series/bars chart positioning
			grid: [
				{
					id: 'lines',
					left: 75,
					right: 75,
					top: 30 + iconSize,
					height: this.linesChartHeight,
					containLabel: false,
				},
				{
					id: 'segments',
					left: 75,
					right: 75,
					top: '77%',
					height: '13%',
					containLabel: false
				},
			],

			tooltip: {
				show: true,
				trigger: 'axis',
				confine: true,
				// Tooltip builder function for lines chart
				formatter: (params) => {
					// Get actual series data
					const actualOption = this.chartInstance()?.getOption()
					const series = actualOption?.series

					const fractionDigits = params[0].componentSubType == 'line' ? 2 : 0
					const parseData = (data) => {
						return (isNaN(data))
							? '-'
							: Number(data).toFixed(fractionDigits)
					}

					// Get first valid date
					let date = new Date()
					for (let index = 0; index < params.length; index++) {
						if (params[index]) {
							date = new Date(new Date(params[index].axisValue).getTime())
							break
						}
					}

					let tooltip = ''
					// Addtooltip header
					tooltip += `<div class="d-flex justify-content-center">`
					tooltip += `<div>${chartDateStr(date)}</div>`
					tooltip += `</div>`

					tooltip += `<div class="d-flex justify-content-between">`

					let gridLines = []
					for (const param of params) {
						// Exclude confidence band
						if (!param?.data
							|| param.seriesId.includes('confidence_range')
							|| param.seriesId.includes('confidence_line')
						) {
							continue
						}
						
						const val = param?.data[1]
						const isHovered = this.hoveredSeries == param.seriesId
						let valMinText = '-'
						let valMaxText = '-'

						const time = new Date(new Date(param.axisValue).getTime())
						
						// Get confidence band values from series data
						const valMin = series
							?.find((el) => el.id == (param.seriesId + 'confidence_line'))
							?.data
							?.find((el) => el[0].getTime() == param.data[0].getTime())[1]
						const valRange = series
							?.find((el) => el.id == (param.seriesId + 'confidence_range'))
							?.data
							?.find((el) => el[0].getTime() == param.data[0].getTime())[1]
						if (Number(val) >= 0) {
							valMinText = parseData(valMin)
							valMaxText = parseData(
								(Number(valMin) + Math.abs(Number(valRange)))
							)
						} else {
							valMinText = parseData((Number(valMin) + Number(valRange)))
							valMaxText = parseData(valMin)
						}

						gridLines.push([
							`<div class="text-start fw-bolder ${isHovered ? 'bg-secondary-subtle' : ''}">
								<span style="color: ${param.color};">&#9210; </span>
								${param.seriesName}
							</div>`,
							`<div class="px-3 ${isHovered ? 'bg-secondary-subtle' : ''}">${chartTimeStr(time, true)}</div>`,
							`<div class="text-end ${isHovered ? 'bg-secondary-subtle' : ''}">
							${parseData(val)}
							${(valMinText != '-' || valMaxText != '-') ? `(мін = ${valMinText}, макс = ${valMaxText})` : ''}
							</div>`,
						])
					}

					// Column 1
					tooltip += `<div>`
					for (const line of gridLines) {
						tooltip += line[0]
					}
					tooltip += `</div>`
					// Column 2
					tooltip += `<div>`
					for (const line of gridLines) {
						tooltip += line[1]
					}
					tooltip += `</div>`
					// Column 3
					tooltip += `<div>`
					for (const line of gridLines) {
						tooltip += line[2]
					}
					tooltip += `</div>`

					// Close flexbox
					tooltip += `</div>`

					return tooltip
				}
			},

			toolbox: [
				{
					show: true,
					top: '81%',
					right: 15,
					orient: 'vertical',
					itemSize: 16,
					feature: {
						myFlipConnectionChart: {
							show: true,
							title: 'Змінити тип діаграми зв\'язку',
							icon: 'path://M -0.85 15.85 L 20.85 -2.85 L 21.35 -2.35 L -0.35 16.35 L -0.85 15.85 Z M -3 -3 L 10 -3 L 10 -1 L -3 -1 L -3 -3 Z M -3 3 L 4 3 L 4 5 L -3 5 L -3 3 Z M -3 9 L -1 9 L -1 11 L -3 11 L -3 9 Z M 20 5 L 22 5 L 22 18 L 20 18 L 20 5 Z M 14 11 L 16 11 L 16 18 L 14 18 L 14 11 Z M 8 16 L 10 16 L 10 18 L 8 18 L 8 16 Z',
							iconStyle: {
								opacity: 0.3,
								color: 'currentColor'
							},
							onclick: () => {
								this.flipConnectionCharts()
							},
						},
						myShowTable: {
							show: true,
							title: 'Показати таблицю',
							icon: 'path://' + this.showTableIcon,
							iconStyle: {
								opacity: 0.3, // don't know, but need for right opacity controll
								color: 'currentColor'
							},
							onclick: () => {
								this.toggleTable()
							},
						},
					},
				},
			],

			xAxis: [
				xAxisOptions('line_top', this.$options.LINES_CHART_GRID_INDEX, 'top'),
				xAxisOptions('line_bottom', this.$options.LINES_CHART_GRID_INDEX, 'bottom'),
				{
					id: 'bar',
					data: [],
				},
				{
					id: 'segments',
					gridIndex: this.$options.SEGMENTS_CHART_GRID_INDEX,
					min: 0,
					max: (this.periodTo.getTime() - this.periodFrom.getTime()) / seriesXaxisDivider,
					boundaryGap: false,			
					animation: false,
					axisLine: {
						show: true, // for compatibility with change yAxis settings on click
					},
					axisTick: {
						show: false, // for compatibility with change yAxis settings on click
					},
					axisLabel: {
						formatter: (time) => {
							const date = new Date(
								this.periodFrom.getTime()
								+ time * seriesXaxisDivider
							)
							return timeLabelFormatter(
								this.chartsZoomFrom,
								date,
								this.chartsZoomTo
							)
						},
						hideOverlap: true, // prevent overlpping
						showMinLabel: true,
						showMaxLabel: true,
					},
				},
			],

			yAxis: [
				yAxisOptions('line_left', this.$options.LINES_CHART_GRID_INDEX, 'left'),
				yAxisOptions('line_right', this.$options.LINES_CHART_GRID_INDEX, 'right'),
				{
					id: 'segments',
					gridIndex: this.$options.SEGMENTS_CHART_GRID_INDEX,
					z: 2,
					axisLine: { show: false }, // disable axis line
					axisTick: { show: false }, // disable axis ticks
					axisLabel: {
						inside: true,
						margin: -43,
						hideOverlap: true,
						fontFamily: 'monospace',
					},
					animation: false,
					data: this.segmentsArray.map((el) => el.title)
				},
			],

			series: [
				...lineSeriesGenerator(),
				{
					id: 'flag_icons',
					type: 'custom',
					xAxisId: 'line_top',
					renderItem: this.renderFlagIcon,
					data: [],
					tooltip: {
						trigger: 'item',
						confine: true,
						formatter: this.renderFlagTooltip
					},
				},
				// For connectionMetrics and connectionAndRanges
				{
					id: 'segments',
					xAxisId: 'segments',
					yAxisId: 'segments',
					type: 'custom',
					animation: false,
					renderItem: renderConnectionChartItem,
					data: [],
					tooltip: {
						trigger: 'item',
						confine: true,
						formatter: (params) => {
							let tooltip = ''
							const timeBegin = new Date(
								this.periodFrom.getTime()
								+ params.value[0] * seriesXaxisDivider
							)
							const timeEnd = new Date(
								this.periodFrom.getTime()
								+ params.value[0] * seriesXaxisDivider
								+ params.value[2] * seriesXaxisDivider
							)
							const period = periodDays(timeBegin, timeEnd)
							
							if (params.data[5] == 'connectionAndRanges') {
								const segment = this.segmentsArray.find((v) => v.title == params.value[1])
								tooltip += `<div>`
								// Set state depend on metric
								if (segment.metric == ModemUnit.metricKeys.isConnect
									|| segment.metric == ServiceUnit.metricKeys.isConnect
									|| segment.metric == PowerUnit.metricKeys.isConnect
								) {
									tooltip += `<b>Стан:</b> ${params.value[4] ? `є зв'язок` : `немає зв'язку`}<br>`
								} else if (
									segment.metric == ServiceUnit.metricKeys.u220
									|| segment.metric == ServiceUnit.metricKeys.tOutdoor
								) {
									tooltip += `<b>Стан:</b> ${params.value[4] ? `в діапазоні` : `за межами діапазону`}<br>`
								}

								tooltip += `
									<b>Час фіксації:</b> 
									${chartDateStr(timeBegin)} ${chartTimeStr(timeBegin)} 
									- ${period > 1.0 ? chartDateStr(timeEnd) : ''} 
									${chartTimeStr(timeEnd)}<br>
								`
								tooltip += `</div>`
								
								return tooltip
							} else if (params.data[5] == 'connectionMetrics') {
								const parseData = (data) => isNaN(data) ? '-' : data
								// const date = new Date(params[0].axisValue);
								// var local = new Date(date.getTime())

								// Addtooltip header
								let tooltip = `<div class="container">`
								tooltip += `<div class="row"><div class="col text-center">`
								if (period >= 1.0) {
									tooltip += `${chartDateStr(timeBegin)} - ${chartDateStr(timeEnd)}`
								} else {
									tooltip += `${chartDateStr(timeBegin)}, ${chartTimeStr(timeBegin)} - ${chartTimeStr(timeEnd)}`
								}
								tooltip += `</div></div>`

								// Add tooltip body
								if (!isNaN(params.value[11])) {
									tooltip += `<div class="row justify-content-between">`
									tooltip += `<div class="col ms-0 text-start"><span style="color: ${GRAY};">&#9210;</span> Період опитування станції, c</div>`
									tooltip += `<div class="col-auto ms-auto me-0 text-end fw-bolder">${verboseTimePeriod(params.value[11], true)}</div>`
									tooltip += `</div>`
								}
								
								for (const bar of Object.values(BARS)) {
									tooltip += `<div class="row justify-content-between">`
									tooltip += `<div class="col ms-0 text-start"><span style="color: ${bar.color};">&#9210;</span> ${bar.text}</div>`
									tooltip += `<div class="col-auto ms-auto me-0 text-end fw-bolder">${parseData(params.value[bar.valueIndex])}</div>`
									tooltip += `</div>`
								}
								tooltip += `</div>`

								return tooltip
							}
							
						},
					},
				},
				// Vertical line marker at point of interest (poi) on top chart
				{
					id: 'poi_marker',
					type: 'line',
					xAxisId: 'line_top',
					yAxisId: 'line_left',
					data: [],
					silent: true,
					showSymbol: false,
					markArea: {
						silent: true,
						animation: false,
						data: [],
						itemStyle: {
							color: 'rgba(92, 99, 106, 0.1)',
							borderWidth: 0,
						},
						label: { show: false },
					},
				},
			]
		}
	},

	async mounted() {
		this.initCharts()

		await this.fetchMetrics()
		this.setPoi(this.chartZoomMiddle)

		// Need for show chart on return to this tab
		this.$nextTick(() => {
			this.updateAllCharts()
		})
	},

	methods: {
		initCharts() {
			// Undefine event handlers
			//
			this.chartInstance()?.off('click')
			this.chartInstance()?.off('datazoom')
			this.chartInstance()?.off('mouseover')
			this.chartInstance()?.off('mouseout')
			this.chartInstance()?.getZr()?.off('click')
			this.chartInstance()?.getZr()?.off('dblclick')

			// Set chart options
			//
			this.option.xAxis[0].min = this.periodFrom
			this.option.xAxis[0].max = this.periodTo
			this.option.xAxis[1].min = this.periodFrom
			this.option.xAxis[1].max = this.periodTo
			this.option.xAxis[3].min = 0
			this.option.xAxis[3].max = (this.periodTo.getTime() - this.periodFrom.getTime()) / seriesXaxisDivider
			// Change for correct data zoom height on chart rebuild
			this.option.dataZoom[0].height = this.dataZoomHeight
			this.option.grid.find((el) => el.id == 'lines').height = this.linesChartHeight
			// For hide segments when table is showing
			this.option.series.find((el) => el.id == 'segments').xAxisId = this.isTable ? null : 'segments'
			this.option.series.find((el) => el.id == 'segments').yAxisId = this.isTable ? null : 'segments'
			// Show only checked lines
			this.option.legend.selected = Object.fromEntries(this.items.map((el) => [el.title, el.showInLines])),
			this.chartInstance()?.setOption(this.option)

			// Define event handlers in next
			//
			this.$nextTick(() => {
				// On click chart area handler
				this.chartInstance()?.getZr()?.on('click', (params) => {
					// console.log('on click chart area')
					if (this.isLoading) {
						return
					}

					const pixelPoint = [params.event.offsetX, params.event.offsetY]

					// Ignore clicks outside the 'lines' grid area
					if (!this.chartInstance().containPixel({ gridId: 'lines' }, pixelPoint)) {
						return
					}

					// Convert pixel position to Date
					const xValue = this.chartInstance().convertFromPixel(
						{ gridId: 'lines', },
						pixelPoint
					)?.[0]

					if (xValue === undefined || xValue === null || isNaN(xValue)) {
						return
					}

					// Save clicked time (x-axis) to poi
					this.setPoi(new Date(xValue))
				})

				// On doube click in chart area
				this.chartInstance()?.getZr()?.on('dblclick', async (params) => {
					if (this.isLoading) {
						return
					}

					const xValue = this.chartInstance()?.convertFromPixel(
						{ gridId: 'lines', },
						[params.event.offsetX, params.event.offsetY]
					)[0]

					this.onChartDblClick(xValue)
				})

				// On chart zoom
				this.chartInstance()?.on('datazoom', (/*params*/) => {
					// console.log('on datazoom')
					const series = this.chartInstance().getOption().series
					// Update icons
					const seriesData = series.find((el) => el.id == 'flag_icons').data
					if (!seriesData || this.flags.length == 0) {
						return
					}
					seriesData.length = 0
					this.groupFlagsToIcons(seriesData)

					// Apply new data to chart
					this.chartInstance().setOption({ series: series, })
				})

				// On line mouseover
				this.chartInstance()?.on('mouseover', ({ seriesId }) => {
					this.hoveredSeries = seriesId
				})

				// On line mouseout
				this.chartInstance()?.on('mouseout', () => {
					this.hoveredSeries = null
				})
			})
		},

		updateAllCharts() {
			this.updateLinesChart(this.chartsZoomFrom, this.chartsZoomTo)
			this.updateFlags(this.chartsZoomFrom, this.chartsZoomTo)
			this.updateSegmentsChart(this.chartsZoomFrom, this.chartsZoomTo)
		},

		updateFlags(timeFrom, timeTo) {
			// console.log('update flags', timeFrom, timeTo)
			const actualOption = this.chartInstance().getOption()
			let series = actualOption.series

			// Iterate all flags
			for (const flagConf of this.visibleFlagItemsConf) {
				// If lightning icons
				if (flagConf.metric == 'lightning') {
					if (this.lightnings === undefined || this.lightnings.length == 0) {
						continue
					}

					for (const lightning of this.lightnings) {
						// If no item on current flag time point
						if (
							this.flags.findIndex((el) =>
								el.time.getTime() == lightning[eventSchema.payload.allowed.flashTimeUTC.key].getTime()
							) == -1
						) {
							this.flags.push(Object.assign(
								{},
								{
									time: lightning[eventSchema.payload.allowed.flashTimeUTC.key],
									metricId: flagConf.metric,
									value: lightning,
								},
								flagConf,
							))
						}
					}

					continue
				}

				// Other (not lightning) flags
				const flagMetrics = this.historyStore.getMetricHistory(
					this.metricsId,
					flagConf.unit,
					flagConf.metric,
					timeFrom,
					timeTo,
				)

				if (flagMetrics.length == 0) {
					continue
				}

				// Search for instances where flag value changes
				let pervFlagValue = flagMetrics[0].value
				// Iterate metrics of current flag
				for (let i = 0; i < flagMetrics.length; i++) {
				// for (const point of flagMetrics) {
					const point = flagMetrics[i]
					if (point.value == pervFlagValue) {
						continue
					} else {
						// console.log(`${flagConf.title} flag change from ${pervFlagValue} to ${point.value} at ${dateTimeToString(point.time)}`)
						pervFlagValue = point.value
					}
					// If no item on current flag time point
					if (
						this.flags.findIndex((el) =>
							el.time.getTime() == point.time.getTime()
						) == -1
					) {
						this.flags.push(Object.assign(
							{},
							{
								time: point.time,
								metricId: flagConf.metric,
								value: point.value,
							},
							flagConf,
						))
					}
				}
			}

			// Sort by time for correct output to chart
			this.flags.sort((a, b) => a.time.getTime() > b.time.getTime())

			const seriesData = series.find((el) => el.id == 'flag_icons').data
			if (!seriesData || this.flags.length == 0) {
				return
			}

			seriesData.length = 0
			this.groupFlagsToIcons(seriesData)

			// Apply new data to chart
			this.chartInstance().setOption({ series: series })
		},

		groupFlagsToIcons(iconSeriesData) {
			const calcX = (time) => {
				return this.chartInstance().convertToPixel(
					{ xAxisId: 'line_top' },
					time
				)
			}

			// Group flags by icon size
			const iconOverlapThreshold = iconSize + iconPadding
			let prevFlag = this.flags[0]
			prevFlag.x = calcX(prevFlag.time)
			this.icons = []
			this.icons.push([Object.assign({}, prevFlag), ])
			let curStack = this.icons[0]
			let curStackX = prevFlag.x
			
			for (let i = 1; i < this.flags.length; i++) {
				const curFlag = this.flags[i]
				curFlag.x = calcX(curFlag.time)
				// let overlapCount = 0

				// If icon overlaps prev icon
				if (Math.abs(curFlag.x - curStackX) < iconOverlapThreshold) {
					curStack.push(curFlag)	// add to stack
				} else {
					// Create new stack and add current icon
					this.icons.push([curFlag])
					curStack = this.icons[this.icons.length - 1]
					// Update cur stack x coordinate
					curStackX = curFlag.x
				}

				prevFlag = this.flags[i]
			}

			// Place icons to series data
			for (let i = 0; i < this.icons.length; i++) {
				const icon = this.icons[i]
				iconSeriesData.push([
					icon[0].time,
					null,
					i,	// icon stack index in this.icons
				])
			}
		},

		updateLinesChart(timeFrom, timeTo) {
			const actualOption = this.chartInstance().getOption()
			let series = actualOption.series
			// console.log('updateLinesChart', timeFrom.getHours(), timeTo.getHours(), this.visibleLinesArray)

			for (const line of this.visibleLinesArray) {
				let data = []
				// Fill previous data if no data in current time point
				let prevValue = undefined
				let prevMinValue = undefined
				let prevMaxValue = undefined
				const makeDataPoint = (time, val, minVal, maxVal) => {
					let finalVal, finalMin, finalMax
					
					// Fill median value
					if (isNaN(val) && this.fillUndefinedAsPrevious && prevValue) {
						finalVal = prevValue
					} else if (!isNaN(val)) {
						finalVal = val
						prevValue = val
					} else {
						finalVal = undefined
					}

					// Fill minimum value
					if (isNaN(minVal) && this.fillUndefinedAsPrevious && prevMinValue) {
						finalMin = prevMinValue
					} else if (!isNaN(minVal)) {
						finalMin = minVal
						prevMinValue = minVal
					} else {
						finalMin = undefined
					}

					// Fill maximum value
					if (isNaN(maxVal) && this.fillUndefinedAsPrevious && prevMaxValue) {
						finalMax = prevMaxValue
					} else if (!isNaN(maxVal)) {
						finalMax = maxVal
						prevMaxValue = maxVal
					} else {
						finalMax = undefined
					}

					return [time, finalVal, finalMin, finalMax]
				}

				const lineMetricsArr = this.historyStore.getMetricHistory(
					this.metricsId,
					line.unit,
					line.metric,
					timeFrom,
					timeTo,
				)

				if (lineMetricsArr.length == 0) {
					continue
				}

				data = lineMetricsArr.map((el) => {
					return makeDataPoint(el.time, el.value ?? el.median, el.min, el.max)
				})
				
				const seriesData = series.find((el) => el.id == line.metric)?.data
				const seriesConfidenceLine = series.find((el) => el.id == line.metric + 'confidence_line')?.data
				const seriesConfidenceRagne = series.find((el) => el.id == line.metric + 'confidence_range')?.data
				if (data?.length <= 0 || !seriesData) {
					continue
				}

				if (this.resetMetricsData) {
					// console.log('Reset metrics data')
					seriesData.length = 0
					if (seriesConfidenceLine) {
						seriesConfidenceLine.length = 0
					}
					if (seriesConfidenceRagne) {
						seriesConfidenceRagne.length = 0
					}
				}

				// Iterate data points, add only new points
				for (const newEl of data) {
					// Main line
					if (seriesData.find((el) => el[0].getTime() == newEl[0].getTime()) === undefined) {
						seriesData.push([newEl[0], newEl[1]])
					}
					
					// Confidence band lines
					if (seriesConfidenceLine
						&& seriesConfidenceLine.find((el) => el[0].getTime() == newEl[0].getTime()) === undefined
					) {
						if (newEl[1] < 0) {
							seriesConfidenceLine.push([newEl[0], newEl[3]])
						} else {
							seriesConfidenceLine.push([newEl[0], newEl[2]])
						}
					}
					if (seriesConfidenceRagne
						&& seriesConfidenceRagne.find((el) => el[0].getTime() == newEl[0].getTime()) === undefined
					) {
						let range = Math.abs(Number(newEl[3]) - Number(newEl[2]))
						if (newEl[1] < 0) {
							range = range == 0 ? range : (range * -1.0)
						}
						seriesConfidenceRagne.push([
							newEl[0],
							range,
						])
					}
				}
				
				// Sort lines by time
				seriesData.sort((a, b) => a[0].getTime() > b[0].getTime())
				if (seriesConfidenceLine) {
					seriesConfidenceLine.sort((a, b) => a[0].getTime() > b[0].getTime())
				}
				if (seriesConfidenceRagne) {
					seriesConfidenceRagne.sort((a, b) => a[0].getTime() > b[0].getTime())
				}
			}

			// Apply new data to chart
			this.chartInstance().setOption(
				{
					series: series,
				},
			)
		},

		updateSegmentsChart(timeFrom, timeTo) {
			const series = this.chartInstance().getOption().series
			const yAxis = this.chartInstance().getOption().yAxis
			const yAxisSegmentsData = yAxis.find((el) => el.id == 'segments').data
			const segmentsSeriesData = series.find((el) => el.id == 'segments').data
			segmentsSeriesData.length = 0
			barsYAxisMax = 0
			barsSurveyPeriodMax = 0
		
			// console.log('update segments', timeFrom, timeTo)
			if (this.segmentsChartType == 'connectionMetrics') {
				const yAxisTitle = 'Якість\nз\'язку'
				// Calculate windows for chart bars
				const windows = calculateTimeWindows(timeFrom, timeTo).windows
				// Prepare map container for chart data
				const connectionAnalyticMetrics = windows.map((el) => { 
					return {
						timeStart: el[0],
						timeEnd: el[1],
						[Station.analyticMetrics.failConnectCntr]: 0,
						[Station.analyticMetrics.failReadCntr]: 0,
						[Station.analyticMetrics.successTransactionCntr]: 0,
						[Station.analyticMetrics.invalidMessagesReceivedCntr]: 0,
						[Station.analyticMetrics.unexpectedlyClosedConnectionCntr]: 0,
					}
				})

				// Fill map container from historyStore
				for (const metric of Object.values(Station.analyticMetrics)) {
					const connectionMetric = this.historyStore.getMetricHistory(
						this.metricsId,
						INFLUXDB.station_metrics.modem_unit,
						metric,
						timeFrom,
						timeTo,
					)
					// console.log(`connectionMetric ${metric}`, connectionMetric)
					for (let i = 0; i < connectionMetric.length; i++) {
						const curMetric = connectionMetric[i]

						// Find window
						const windowIndex = connectionAnalyticMetrics.findIndex(
							(el) => el.timeStart <= curMetric.time && el.timeEnd >= curMetric.time
						)
						if (windowIndex == -1) {
							console.error('Cant find window for metric')
							continue
						}
						
						connectionAnalyticMetrics[windowIndex][metric] += curMetric.value
					}
				}

				// Make chart bars
				for (let i = 0; i < connectionAnalyticMetrics.length; i++) {
					const metric = connectionAnalyticMetrics[i]
					const metricDuraion = Math.floor(
						(metric.timeEnd.getTime() - metric.timeStart.getTime()) / seriesXaxisDivider
					)
					// console.log('metricDuraion', metricDuraion)
					const metricsSum = metric[Station.analyticMetrics.successTransactionCntr] +
						metric[Station.analyticMetrics.failConnectCntr] +
						metric[Station.analyticMetrics.failReadCntr] + 
						metric[Station.analyticMetrics.invalidMessagesReceivedCntr] +
						metric[Station.analyticMetrics.unexpectedlyClosedConnectionCntr]

					// Skip empty bars
					if (metricsSum == 0) {
						continue
					}

					let surveyPeriodSetting = this.passportHistory
						?.findLast((el) => el[FIELD_NAMES.updateTime].getTime() <= metric.timeEnd.getTime())
					
					surveyPeriodSetting = surveyPeriodSetting?.[Station.passportKeys.surveyPeriod] ?? undefined
					barsSurveyPeriodMax = Math.max(surveyPeriodSetting || 0, barsSurveyPeriodMax || 0)

					// Save Max y Axis Value
					barsYAxisMax = Math.max(barsYAxisMax,metricsSum)

					segmentsSeriesData.push([
						Math.floor((metric.timeStart.getTime() - this.periodFrom.getTime()) / seriesXaxisDivider),
						yAxisTitle,
						metricDuraion,
						null,
						null,
						'connectionMetrics',
						metric[Station.analyticMetrics.successTransactionCntr],
						metric[Station.analyticMetrics.failConnectCntr],
						metric[Station.analyticMetrics.failReadCntr],
						metric[Station.analyticMetrics.invalidMessagesReceivedCntr],
						metric[Station.analyticMetrics.unexpectedlyClosedConnectionCntr],
						surveyPeriodSetting,
					])
				}

				// Set y axis data for this scenario
				yAxisSegmentsData.length = 0
				yAxisSegmentsData.push(yAxisTitle)
			}

			if (this.segmentsChartType == 'connectionAndRanges') {
				// Fill metrics one by one
				for (const segment of this.visibleSegmentsArray) {
					if (segment.metric == ModemUnit.metricKeys.isConnect
						|| segment.metric == ServiceUnit.metricKeys.isConnect
						|| segment.metric == PowerUnit.metricKeys.isConnect
					) {
						const connectionMetrics = this.historyStore.getMetricHistory(
							this.metricsId,
							segment.unit,
							segment.metric,
							timeFrom,
							timeTo,
						)

						for (let i = 0; i < connectionMetrics.length - 1; i++) {
							const matric = connectionMetrics[i]
							const nextMetric = connectionMetrics[i + 1]
							const barDuraion = nextMetric
								? Math.floor((nextMetric.time.getTime() - matric.time.getTime()) / seriesXaxisDivider)
								: 10

							segmentsSeriesData.push([
								// x coordinate
								Math.floor((matric.time.getTime() - this.periodFrom.getTime()) / seriesXaxisDivider),
								// y coordinate
								segment.title,
								barDuraion,
								segment.isMetricInRange(matric.value) == true && segment.isMetricInRange(nextMetric?.value) == true
									? GREEN
									: RED,
								segment.isMetricInRange(matric.value) == true && segment.isMetricInRange(nextMetric?.value) == true,
								'connectionAndRanges',
							])
						}
					} else if (segment.metric == ServiceUnit.metricKeys.u220
						|| segment.metric == ServiceUnit.metricKeys.tOutdoor
					) {
							const data = this.historyStore.getMetricHistory(
							this.metricsId,
							segment.unit,
							segment.metric,
							timeFrom,
							timeTo,
						)
						
						// Iterate over values
						for (let i = 0; i < data.length - 1; i++) {
							const metric = data[i]
							const nextMetric = data[i + 1]
						
							const isMetricInRange = segment.isMetricInRange(metric.value ?? metric.median)
							const isNextMetricInRange = segment.isMetricInRange(nextMetric.value ?? nextMetric.median)
							const metricDuraion = Math.floor((nextMetric.time.getTime() - metric.time.getTime()) / seriesXaxisDivider)

							segmentsSeriesData.push([
								// x coordinate
								Math.floor((metric.time.getTime() - this.periodFrom.getTime()) / seriesXaxisDivider),
								// y coordinate
								segment.title,
								metricDuraion,
								isMetricInRange && isNextMetricInRange ? GREEN : RED,
								isMetricInRange && isNextMetricInRange,
								'connectionAndRanges',
							])
						}
					}
				}

				// Set y axis data for this scenario
				yAxisSegmentsData.length = 0
				this.visibleSegmentsArray.toReversed().forEach((el) => {
					yAxisSegmentsData.push(el.title)
				})
			}

			// Apply new data to chart
			this.chartInstance()?.setOption(
				{
					yAxis: yAxis,
					series: series,
				},
			)
		},

		async updateZoomedArea() {
			// console.log('updateZoomedArea')
			
			const dataZoom = this.chartInstance().getOption().dataZoom[0]
			// const startValue = dataZoom?.startValue
			// const endValue = dataZoom?.endValue
			const startPercent = dataZoom?.start
			const endPercent = dataZoom?.end

			this.chartPositionPercent = startPercent + (endPercent - startPercent) / 2
			this.chartRangePercent = endPercent - startPercent

			// Read metrics
			await this.fetchMetrics()
		},

		async fetchMetrics() {
			const metrics = [
				...this.visibleLinesArray.map((el) => [el.unit, el.metric]),
				...this.visibleSegmentsArray.map((el) => [el.unit, el.metric]),
				...this.visibleFlagItemsConf.map((el) => [el.unit, el.metric])
					.filter((el) => el.unit !== undefined && el.metric !== undefined),
				...Object.values(Station.analyticMetrics).map((el) => [INFLUXDB.station_metrics.modem_unit, el]),
			]
			// console.log(
			// 	`fetchMetrics from ${dateTimeToString(this.chartsZoomFrom)} to ${dateTimeToString(this.chartsZoomTo)}`, 
			// 	metrics
			// )

			this.setLoading(true)
			// this.loadLightnings(this.chartsZoomFrom, this.chartsZoomTo)
			await this.historyStore.loadMetrics(
				this.metricsId,
				metrics,
				this.chartsZoomFrom,
				this.chartsZoomTo,
				true,
			)
			this.setLoading(false)
		},

		setLoading(loadingState) {
			//Set variable
			this.isLoading = Boolean(loadingState)
			// console.log('set loading to ', this.isLoading)

			// Emit
			this.$emit('isLoading', this.isLoading)

			// Update chart
			this.chartInstance()?.setOption({
				toolbox: {
					feature: {
						myFlipConnectionChart: {
							iconStyle: {
								opacity: this.isLoading ? 0.3 : 1,
								cursor: this.isLoading ? 'not-allowed' : 'pointer',
							},
						},

						myShowTable: {
							iconStyle: {
								opacity: this.isLoading ? 0.3 : 0.8,
								cursor: this.isLoading ? 'not-allowed' : 'pointer',
							},
						}
					},
				},
			})
		},

		setDataZoomToDefault() {
			// console.log('zoom to default')
			// Back data zoom to default state
			this.chartInstance()?.dispatchAction({
				type: 'dataZoom',
				start: 0,
				end: 100,
			})
		},

		flipConnectionCharts() {
			if (this.isLoading) {
				return
			}

			if (this.segmentsChartType == 'connectionAndRanges') {
				this.segmentsChartType = 'connectionMetrics'
			} else if (this.segmentsChartType == 'connectionMetrics') {
				this.segmentsChartType = 'connectionAndRanges'
			}

			this.setDataZoomToDefault()
			this.updateSegmentsChart(this.periodFrom, this.periodTo)
		},

		toggleTable() {
			if (this.isLoading) {
				return
			}

			this.isTable = !this.isTable
			this.$emit('isTable', this.isTable)
			
			this.setPoi(this.chartZoomMiddle)

			const showSegments = !this.isTable
			this.chartInstance()?.setOption({
				toolbox: {
					feature: {
						myShowTable: {
							title: this.isTable ? 'Приховати таблицю' : 'Показати таблицю',
							icon: 'path://' + this.showTableIcon,
						},
					},
				},
				dataZoom: [
					{
						type: 'slider',
						height: this.dataZoomHeight,
					},
				],
				grid: [
					{
						id: 'lines',
						height: this.linesChartHeight,
					},
					{
						id: 'segments',
						show: showSegments,
					},
				],
				xAxis: [
					{
						id: 'segments',
						show: showSegments,
					},
				],
				yAxis: [
					{
						id: 'segments',
						show: showSegments,
					},
				],
				series: [
					{
						id: 'segments',
						xAxisId: this.isTable ? null : 'segments',
						yAxisId: this.isTable ? null : 'segments',
					},
				],
			})
		},

		setPoi(newPoi) {
			this.poi = this.isTable ? newPoi : null
			if (this.isTable) {
				this.$emit('pointOfInterest', this.poi)
			}

			// Draw / clear vertical poi marker on top chart
			let markArea
			if (this.poi === null) {
				markArea = { data: [] }
			} else {
				const startTime = this.poi.getTime() - this.poiRangeSeconds * 1000
				const endTime = this.poi.getTime() + this.poiRangeSeconds * 1000
				markArea = { data: [[{ xAxis: startTime }, { xAxis: endTime }]] }
			}

			this.chartInstance()?.setOption({
				series: [{
					id: 'poi_marker',
					markArea,
				}],
			})
		},

		onLineClicked(line) {
			this.chartInstance().setOption(
				{
					yAxis: [
						{
							id: line.yAxis.id,
							show: true,
							name: line.title,
							axisLabel: {
								formatter: line.yAxis.formatter,
							},
							nameTextStyle: {
								color: line.lineColor,
							},
						}
					],
				},
			)
		},

		onChartDblClick(xAxisPosition) {
			if (this.isLoading) {
				return
			}

			// Iterate over ranges
			// Choose next range
			const rangeSteps = [100, 50, 25, 10, 5, 1, 0.1]
			this.chartZoomStep = rangeSteps[this.chartZoomStep + 1] !== undefined
				? this.chartZoomStep + 1
				: 0

			this.chartRangePercent = rangeSteps[this.chartZoomStep]
			this.chartPositionPercent = (xAxisPosition - this.periodFrom.getTime())
				/ (this.periodTo.getTime() - this.periodFrom.getTime())
				* 100

			this.chartInstance().dispatchAction({
				type: 'dataZoom',
				// percentage of starting position; 0 - 100
				start: this.chartsZoomFromPercent,
				// percentage of ending position; 0 - 100
				end: this.chartsZoomToPercent,
			})

			this.updateZoomedArea()
		},

		renderFlagIcon(params, api) {
			const addIcons = (icons) => {
				const ret = []
				for (const icon of icons) {
					const size = icon.size ?? iconSize
					// Icon
					ret.push({
						type: 'path',
						shape: {
							pathData: icon.path,
							x: -size / 2 + (icon.shiftX ?? 0),
							y: -size / 2 + (icon.shiftY ?? 0),
							width: size,
							height: size,
						},
						style: {
							fill: icon.color,
							opacity: icon.opacity ?? 0.6,
							transition: 'opacity',
							// Shadow
							shadowBlur: 1,
							shadowColor: 'rgba(0,0,0,0.2)',
							shadowOffsetX: 1,
							shadowOffsetY: 1,
						},
						emphasis: {
							style: {
								opacity: 1,
								shadowOffsetX: 2,
								shadowOffsetY: 2,
							},
						},
						z2: 1,
						transition: ['style', 'x', 'y'],
					})
				}
				return ret
			}

			const chart = params.coordSys
			const x = api.coord([api.value(0), 0])[0]
			const iconIndex = api.value(2)
			const icons = this.icons[iconIndex]
			const isSameGroup = icons.every((el) => el.group == icons[0].group)
			const iconLines = []

			if (isSameGroup) {
				const group = icons[0].group

				// console.log('flags group', group)
				// console.log('flags group icon index', Number(icons[0].valueToIconCode(icons[0].value)))
				
				const isAllFlagsSameValue = icons.every((el) => el.value == icons[0].value)
				// Add line by line from group to flag icon
				for (const line of group.icons[icons[0].valueToIconCode(icons[0].value)]) {
					iconLines.push(Object.assign(
						{},
						line,
						{ color: isAllFlagsSameValue ? line.color : GRAY },
					))
				}
			} else {
				// Stack icon
				iconLines.push({
					path: 'M640-160v-360H160v360h480Zm80-200v-80h80v-360H320v200h-80v-200q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v360q0 33-23.5 56.5T800-360h-80ZM160-80q-33 0-56.5-23.5T80-160v-360q0-33 23.5-56.5T160-600h480q33 0 56.5 23.5T720-520v360q0 33-23.5 56.5T640-80H160Zm400-603ZM400-340Z',
					color: GRAY
				})
			}

			const y = chart.y - iconSize

			return {
				type: 'group',
				position: [x, y],
				children: [
					// Transparent background for mouse over handler
					{
						type: 'circle',
						shape: {
							cx: 0,
							cy: 0,
							r: iconSize / 2,
						},
						style: {
							fill: 'rgba(0,0,0,0)',
						},
						z2: 0,
						invisible: false,
						silent: false,
					},
					{
						type: 'text',
						style: {
							text: icons.length > 1 ? icons.length : '',
							x: icons.length > 9 ? iconSize / 2 + 15 : iconSize / 2 + 8,
							y: iconSize / 2 + 6,
							fill: GRAY,
							font: 'bold 12px sans-serif',
							textAlign: 'right',
							textVerticalAlign: 'bottom',
						},
						z2: 2,
					},
				].concat(addIcons(iconLines)),
			}
		},

		renderFlagTooltip(params) {
			// console.log('tooltip parms', params)
			const parseValue = (v, metric) => {
				if (metric == PowerUnit.metricKeys.stationStabilizationMode) {
					return PowerUnit.stabilizationModeText(v)
				} else if (metric == ServiceUnit.metricKeys.fSecurityCircuitBoxDoor) {
					return ServiceUnit.boxDoorStatePromt(v)
				} else if (metric == 'lightning') {
					return `${v.distance_km.toFixed(1)} км`
				} else {
					return v ? 'Є' : 'НЕМАЄ'
				}
			}

			const icons = this.icons[params.value[2]]
			// console.log('tooltip icons', icons)
			let tooltip = `<div class="container">`
			if (icons.length == 1) {
				const flag = icons[0]
				// Show one flag icon
				// Addtooltip header
				tooltip += `<div class="row"><div class="col text-center">`
				tooltip += ` ${chartDateStr(flag.time)} ${chartTimeStr(flag.time, true)}`
				tooltip += `</div></div>`
				// Tooltip body
				tooltip += `<div class="row justify-content-between">`
					tooltip += `<div class="col my-0 py-0 text-start">`
						tooltip += flag.title
					tooltip += `</div>`
					tooltip += `<div class="col-auto my-0 py-0 ms-auto me-0 text-end fw-bolder">`
						tooltip += parseValue(flag.value, flag.metricId)
					tooltip += `</div>`
				tooltip += `</div>`
			} else {
				// Iterate all flags
				for (const flag of icons) {
					// Tooltip body
					tooltip += `<div class="row justify-content-between">`
					tooltip += `<div class="col-auto my-0 py-0 text-start">`
						tooltip += `${chartDateStr(flag.time)} ${chartTimeStr(flag.time, true)}`
					tooltip += `</div>`

					tooltip += `<div class="col-auto my-0 py-0 ms-0 me-auto text-start">`
						tooltip += flag.title
					tooltip += `</div>`

					tooltip += `<div class="col-auto my-0 py-0 ms-auto me-0 text-end fw-bolder">`
						tooltip += parseValue(flag.value, flag.metricId)
					tooltip += `</div>`
					tooltip += `</div>`
					
				}
			}
			
			tooltip += `</div>`
			// Set state depend on metric
			return tooltip
		},

		buildSideDots() {
			const dotSpacing = 20
			const topOffset = 30 + iconSize + 5

			const leftLines = this.items.filter(l => l.yAxis?.side === 'left')
			const rightLines = this.items.filter(l => l.yAxis?.side === 'right')

			return [
				{
					type: 'group',
					left: 2,
					top: topOffset,
					children: leftLines.map((line, i) => ({
						type: 'text',
						x: 0,
						y: i * dotSpacing,
						style: {
							text: '⏺',
							fill: line.lineColor,
							font: '16px sans-serif',
							opacity: line.showInLines ? 0.8 : 0,
						},
						cursor: 'pointer',
						onclick: () => { this.onLineClicked(line) },
					})),
				},

				{
					type: 'group',
					right: 2,
					top: topOffset,
					children: rightLines.map((line, i) => ({
						type: 'text',
						x: 0,
						y: i * dotSpacing,
						style: {
							text: '⏺',
							fill: line.lineColor,
							font: '16px sans-serif',
							opacity: line.showInLines ? 0.8 : 0,
						},
						cursor: 'pointer',
						onclick: () => { this.onLineClicked(line) },
					})),
				},
			]
		},

		chartInstance() { return this.$refs.chart?.chart },
	},

	computed: {
		station() {
			return this.stationsStore.station(this.stationId)
		},

		metricsId() {
			return this.station.passport.get(Station.passportKeys.metricsId)
		},

		updateTriger() {
			return this.historyStore.updateTriger
		},
		
		dataZoomHeight() {
			return this.isTable ? 20 : 40
		},

		linesChartHeight() {
			return this.isTable ? '58%' : '68%'
		},

		linesArray() {
			return this.items.filter((el) => Object.hasOwn(el, 'showInLines'))
		},

		visibleLinesArray() {
			return this.items.filter((el) => el.showInLines == true)
		},

		segmentsArray() {
			return this.items.filter((el) => Object.hasOwn(el, 'showInSegments'))
		},

		visibleSegmentsArray() {
			return this.items.filter((el) => el.showInSegments == true)
		},

		lineMetricUnits() {
			return Array.from(
				new Set(
					this.items
					.filter((v) => v.unit)
					.map((v) => v.unit)
				)
			)
		},

		lineMetrics() {
			return Array.from(
				new Set(
					this.items
					.filter((v) => v.metric)
					.map((v) => v.metric)
				)
			)
		},

		flagItemsConf() {
			return Array.from(
				new Set(
					this.items
					.filter((el) => Object.hasOwn(el, 'showInIcons'))
				)
			)
		},

		visibleFlagItemsConf() {
			return this.flagItemsConf.filter((el) => el.showInIcons)
		},

		flagUnits() {
			return this.flagItemsConf.map((v) => v.unit)
		},

		flagMetrics() {
			return this.flagItemsConf.map((v) => v.metric)
		},

		lightnings() {
			return this.lightningEvents.map((event) => event[eventSchema.payload.key])
		},

		msInPercent() {
			return (this.periodTo.getTime() - this.periodFrom.getTime()) / 100
		},

		chartsZoomFromPercent() {
			if (this.chartRangePercent == 100) {
				return 0
			}

			let zoomFromPercent = this.chartPositionPercent - this.chartRangePercent / 2
			
			if (zoomFromPercent < 0) {
				zoomFromPercent = 0
			}

			return zoomFromPercent
		},

		chartsZoomFrom() {
			return new Date(
				this.periodFrom.getTime() + this.chartsZoomFromPercent * this.msInPercent
			)
		},
		
		chartsZoomToPercent() {
			if (this.chartRangePercent == 100) {
				return 100
			}

			let zoomToPercent = this.chartPositionPercent + this.chartRangePercent / 2
			
			if (zoomToPercent > 100) {
				zoomToPercent = 100
			}

			return zoomToPercent
		},

		chartsZoomTo() {
			return new Date(
				this.periodFrom.getTime() + this.chartsZoomToPercent * this.msInPercent
			)
		},

		// The middle of chart time
		chartZoomMiddle() {
			const middleTime = (this.chartsZoomFrom.getTime() + this.chartsZoomTo.getTime()) / 2
			return new Date(middleTime)
		},

		connectionMetrics() {
			return [
				...Object.values(Station.analyticMetrics),
				ModemUnit.metricKeys.isConnect,
			]
		},

		showTableIcon() {
			if (this.isTable) {
				return 'M0 0h1v15h15v1H0zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07'
			} else {
				return 'M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z'
			}
		},
	},
}
</script>




<style scoped>
</style>